import { useState } from 'react';
import { PayoutRequest } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { DollarSign, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface AgentPayoutsProps {
  payoutRequests: PayoutRequest[];
  onProcessPayout: (payoutId: string, action: 'approve' | 'reject', notes?: string) => Promise<void>;
}

export function AgentPayouts({ payoutRequests, onProcessPayout }: AgentPayoutsProps) {
  const [selectedPayout, setSelectedPayout] = useState<PayoutRequest | null>(null);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [processing, setProcessing] = useState<'approve' | 'reject' | null>(null);

  const pendingPayouts = payoutRequests.filter(p => p.status === 'pending');
  const historyPayouts = payoutRequests.filter(
    p => p.status === 'approved' || p.status === 'rejected' || p.status === 'processed',
  );

  const totalPendingAmount = pendingPayouts.reduce((sum, p) => sum + p.amount, 0);
  const totalProcessedAmount = payoutRequests
    .filter(p => p.status === 'processed' || p.status === 'approved')
    .reduce((sum, p) => sum + p.amount, 0);

  const handleViewPayout = (payout: PayoutRequest) => {
    setSelectedPayout(payout);
    setShowPayoutDialog(true);
    setActionNotes('');
  };

  const handleProcess = async (action: 'approve' | 'reject') => {
    if (!selectedPayout) return;
    setProcessing(action);
    try {
      await onProcessPayout(selectedPayout.id, action, actionNotes || undefined);
      toast.success(`Payout ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowPayoutDialog(false);
      setSelectedPayout(null);
      setActionNotes('');
    } catch {
      // parent already toasted the error
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status: PayoutRequest['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="border-orange-300 text-orange-700 bg-orange-50">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">Approved</Badge>;
      case 'processed':
        return <Badge className="bg-green-600 border-0 text-white">Processed</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Requests</p>
                <p className="text-3xl font-bold text-orange-600">{pendingPayouts.length}</p>
                <p className="text-sm text-gray-400 mt-1">
                  ${totalPendingAmount.toLocaleString()} total
                </p>
              </div>
              <div className="p-3 bg-orange-50 rounded-xl">
                <Clock className="size-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved / Processed</p>
                <p className="text-3xl font-bold text-green-600">{historyPayouts.filter(p => p.status !== 'rejected').length}</p>
                <p className="text-sm text-gray-400 mt-1">
                  ${totalProcessedAmount.toLocaleString()} paid
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl">
                <CheckCircle className="size-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Requests</p>
                <p className="text-3xl font-bold text-blue-600">{payoutRequests.length}</p>
                <p className="text-sm text-gray-400 mt-1">80% commission rate</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <DollarSign className="size-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Payouts Table */}
      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 bg-orange-500 rounded-lg">
              <Clock className="size-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                Pending Payout Requests
                <Badge className="bg-orange-500 text-white border-0">{pendingPayouts.length}</Badge>
              </div>
              <p className="text-sm font-normal text-gray-500 mt-0.5">
                Awaiting admin approval
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-xs">Request ID</TableHead>
                  <TableHead className="font-semibold text-xs">Agent Name</TableHead>
                  <TableHead className="font-semibold text-xs">Agent ID</TableHead>
                  <TableHead className="font-semibold text-xs">Amount</TableHead>
                  <TableHead className="font-semibold text-xs">Request Date</TableHead>
                  <TableHead className="font-semibold text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingPayouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-16">
                      <CheckCircle className="size-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No pending payout requests</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingPayouts.map(payout => (
                    <TableRow key={payout.id} className="hover:bg-orange-50/30 transition-colors">
                      <TableCell className="font-mono text-xs text-gray-500">
                        {payout.id.slice(-8).toUpperCase()}
                      </TableCell>
                      <TableCell className="font-medium text-gray-900">{payout.agentName}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-400">
                        {payout.agentId.slice(-8)}
                      </TableCell>
                      <TableCell className="font-semibold text-green-600">
                        ${payout.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {format(payout.requestedDate, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-orange-200 hover:bg-orange-50 hover:border-orange-400"
                          onClick={() => handleViewPayout(payout)}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payout History (approved + processed + rejected) */}
      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 bg-slate-500 rounded-lg">
              <DollarSign className="size-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                Payout History
                <Badge className="bg-slate-500 text-white border-0">{historyPayouts.length}</Badge>
              </div>
              <p className="text-sm font-normal text-gray-500 mt-0.5">All processed, approved, and rejected payouts</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-xs">Request ID</TableHead>
                  <TableHead className="font-semibold text-xs">Agent Name</TableHead>
                  <TableHead className="font-semibold text-xs">Amount</TableHead>
                  <TableHead className="font-semibold text-xs">Request Date</TableHead>
                  <TableHead className="font-semibold text-xs">Processed Date</TableHead>
                  <TableHead className="font-semibold text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyPayouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No payout history yet
                    </TableCell>
                  </TableRow>
                ) : (
                  historyPayouts.map(payout => (
                    <TableRow key={payout.id} className="hover:bg-gray-50/50 transition-colors">
                      <TableCell className="font-mono text-xs text-gray-500">
                        {payout.id.slice(-8).toUpperCase()}
                      </TableCell>
                      <TableCell className="font-medium text-gray-900">{payout.agentName}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        ${payout.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {format(payout.requestedDate, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {payout.processedDate ? format(payout.processedDate, 'MMM dd, yyyy') : '—'}
                      </TableCell>
                      <TableCell>{getStatusBadge(payout.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payout Review Dialog */}
      <Dialog
        open={showPayoutDialog}
        onOpenChange={open => { if (!processing) setShowPayoutDialog(open); }}
      >
        <DialogContent className="max-w-xl">
          {selectedPayout && (
            <>
              <DialogHeader>
                <DialogTitle>Review Payout Request</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  {[
                    { label: 'Request ID', value: selectedPayout.id, mono: true },
                    { label: 'Agent ID', value: selectedPayout.agentId, mono: true },
                    { label: 'Agent Name', value: selectedPayout.agentName, bold: true },
                    { label: 'Request Date', value: format(selectedPayout.requestedDate, 'MMM dd, yyyy') },
                  ].map(row => (
                    <div key={row.label} className="flex justify-between text-sm">
                      <span className="font-medium text-gray-600">{row.label}:</span>
                      <span className={`${row.mono ? 'font-mono text-xs' : ''} ${row.bold ? 'font-semibold' : ''} text-right max-w-[60%] break-all`}>
                        {row.value}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 border-t border-blue-300">
                    <span className="font-semibold text-gray-800">Payout Amount:</span>
                    <span className="text-2xl font-bold text-green-600">
                      ${selectedPayout.amount.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Notes <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <Textarea
                    placeholder="Add notes about this payout decision…"
                    value={actionNotes}
                    onChange={e => setActionNotes(e.target.value)}
                    rows={3}
                    disabled={!!processing}
                  />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPayoutDialog(false)}
                  disabled={!!processing}
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => handleProcess('reject')}
                  disabled={!!processing}
                >
                  {processing === 'reject' ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="size-4 mr-2" />
                  )}
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleProcess('approve')}
                  disabled={!!processing}
                >
                  {processing === 'approve' ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="size-4 mr-2" />
                  )}
                  Approve Payout
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
